import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  type AdminUser,
  emailUser,
  grantUserPlan,
  listAdminUsers,
  setUserRole,
} from '@/services/api/admin';

// UsersPanel is the admin-side user-management tab.
// Actions supported: promote/demote admin, grant plan (with expiry),
// send one-off email. Search is a simple email substring match.

export function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<{ kind: 'grant' | 'email'; user: AdminUser } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAdminUsers({ q: q.trim() || undefined, limit: 50 });
      setUsers(res.users ?? []);
      setTotal(res.total ?? 0);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { void load(); }, [load]);

  const onToggleRole = async (u: AdminUser) => {
    const next = u.role === 'admin' ? 'user' : 'admin';
    const word = next === 'admin' ? 'Promote' : 'Demote';
    if (!confirm(`${word} ${u.email} to ${next}?`)) return;
    try {
      await setUserRole(u.id, next);
      toast.success(`${word}d ${u.email}`);
      void load();
    } catch (err) {
      toast.error((err as Error).message || 'Role change failed');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="search"
          placeholder="Search by email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 rounded-lg border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand-500/30"
        />
        <span className="text-tiny text-ink-muted tabular-nums">
          {total.toLocaleString()} {total === 1 ? 'user' : 'users'}
        </span>
      </div>

      {loading ? (
        <p className="mt-10 text-center text-sm text-ink-muted">Loading…</p>
      ) : users.length === 0 ? (
        <p className="mt-10 text-center text-sm text-ink-muted">No users match.</p>
      ) : (
        <div className="mt-4 overflow-hidden rounded-lg border border-line-default bg-clyra-raised">
          <table className="w-full text-sm">
            <thead className="bg-clyra-base/50 text-left text-tiny uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Role</th>
                <th className="px-4 py-2 font-medium">Plan</th>
                <th className="px-4 py-2 font-medium">Joined</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-line-subtle">
                  <td className="px-4 py-2 text-ink-primary">{u.email}</td>
                  <td className="px-4 py-2 capitalize text-ink-secondary">{u.role}</td>
                  <td className="px-4 py-2 text-ink-secondary">
                    <span className="uppercase tracking-wider text-tiny">{u.plan}</span>
                    {u.plan_expiry && u.plan !== 'free' && (
                      <span className="ml-2 text-micro text-ink-faint">
                        until {new Date(u.plan_expiry).toLocaleDateString()}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-tiny text-ink-muted">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => setActive({ kind: 'grant', user: u })}
                      className="text-tiny text-teal-600 hover:text-teal-500 [body.paper_&]:text-teal-700 mr-3"
                    >
                      Plan
                    </button>
                    <button
                      onClick={() => setActive({ kind: 'email', user: u })}
                      className="text-tiny text-ink-secondary hover:text-ink-primary mr-3"
                    >
                      Email
                    </button>
                    <button
                      onClick={() => onToggleRole(u)}
                      className="text-tiny text-ink-muted hover:text-ink-primary"
                    >
                      {u.role === 'admin' ? 'Demote' : 'Promote'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active?.kind === 'grant' && (
        <GrantPlanDialog
          user={active.user}
          onClose={() => setActive(null)}
          onDone={() => { setActive(null); void load(); }}
        />
      )}
      {active?.kind === 'email' && (
        <EmailUserDialog
          user={active.user}
          onClose={() => setActive(null)}
          onDone={() => setActive(null)}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function GrantPlanDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [plan, setPlan] = useState<'free' | 'pro' | 'byo'>(user.plan);
  const [days, setDays] = useState('30');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      let expires_at: string | undefined;
      if (plan !== 'free') {
        const d = parseInt(days, 10);
        if (Number.isFinite(d) && d > 0) {
          expires_at = new Date(Date.now() + d * 86400_000).toISOString();
        }
      }
      await grantUserPlan(user.id, plan, { expires_at, note: note.trim() || undefined });
      toast.success(`Granted ${plan} to ${user.email}`);
      onDone();
    } catch (err) {
      toast.error((err as Error).message || 'Grant failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Grant plan — ${user.email}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-petit font-medium text-ink-secondary [body.paper_&]:text-charcoal-soft mb-1.5">
            Plan
          </label>
          <div className="flex rounded-lg border border-line-default overflow-hidden [body.paper_&]:border-stone-default">
            {(['free', 'pro', 'byo'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPlan(p)}
                className={`flex-1 px-3 py-1.5 text-tiny font-medium uppercase tracking-wider transition-colors ${
                  plan === p
                    ? 'bg-brand-500/15 text-brand-400 [body.paper_&]:bg-charcoal [body.paper_&]:text-paper'
                    : 'text-ink-muted hover:text-ink-primary [body.paper_&]:text-charcoal-muted'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        {plan !== 'free' && (
          <div>
            <label className="block text-petit font-medium text-ink-secondary [body.paper_&]:text-charcoal-soft mb-1.5">
              Duration (days)
            </label>
            <input
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full rounded-lg border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500/30"
            />
          </div>
        )}
        <div>
          <label className="block text-petit font-medium text-ink-secondary [body.paper_&]:text-charcoal-soft mb-1.5">
            Note (optional, logged)
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. partnership comp — 90 days"
            className="w-full rounded-lg border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500/30"
          />
        </div>
      </div>

      <ModalActions
        onCancel={onClose}
        onConfirm={onSubmit}
        confirmLabel={plan === 'free' ? 'Revert to Free' : 'Grant plan'}
        loading={submitting}
      />
    </ModalShell>
  );
}

function EmailUserDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      await emailUser(user.id, subject.trim(), body.trim());
      toast.success(`Emailed ${user.email}`);
      onDone();
    } catch (err) {
      toast.error((err as Error).message || 'Send failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Email — ${user.email}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-petit font-medium text-ink-secondary [body.paper_&]:text-charcoal-soft mb-1.5">
            Subject
          </label>
          <input
            type="text"
            maxLength={160}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Quick follow-up"
            className="w-full rounded-lg border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500/30"
          />
        </div>
        <div>
          <label className="block text-petit font-medium text-ink-secondary [body.paper_&]:text-charcoal-soft mb-1.5">
            Message
          </label>
          <textarea
            rows={7}
            maxLength={8000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Plain text. Newlines become paragraphs."
            className="w-full rounded-lg border border-line-default bg-clyra-bg px-3 py-2 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-brand-500/30 resize-none"
          />
          <p className="mt-1 text-micro text-ink-muted">
            Sent from <code>oche@useclyra.com</code>. HTML is stripped.
          </p>
        </div>
      </div>

      <ModalActions
        onCancel={onClose}
        onConfirm={onSubmit}
        confirmLabel="Send email"
        loading={submitting}
        disabled={!subject.trim() || !body.trim()}
      />
    </ModalShell>
  );
}

// Shared skeleton so I don't repeat the backdrop + keyboard handler in every dialog.
function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-line-default bg-clyra-raised p-6 shadow-clyra-lg [body.paper_&]:border-stone-subtle [body.paper_&]:bg-paper-raised"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-ink-primary [body.paper_&]:text-charcoal">{title}</h2>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({
  onCancel,
  onConfirm,
  confirmLabel,
  loading,
  disabled,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="mt-6 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-line-default px-4 py-2 text-tiny font-medium text-ink-secondary hover:border-line-strong [body.paper_&]:border-stone-default [body.paper_&]:text-charcoal-soft [body.paper_&]:hover:border-stone-strong"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onConfirm}
        disabled={loading || disabled}
        className="rounded-lg bg-brand-600 px-4 py-2 text-tiny font-medium text-white hover:bg-brand-500 disabled:opacity-40 [body.paper_&]:bg-charcoal [body.paper_&]:text-paper [body.paper_&]:hover:bg-charcoal-soft"
      >
        {loading ? 'Working…' : confirmLabel}
      </button>
    </div>
  );
}
